import React from 'react';
import {
  C,
  LEADERBOARD_SETTINGS_H,
  LEADERBOARD_SETTINGS_W,
  getPathValue,
  nx,
  ny,
} from '../flowCanvas.constants';

const PAD = 14;
const HEADER_H = 42;
const CHIP_H = 28;
const TOGGLE_H = 28;

const MODE_OPTIONS = [
  { key: 'manual', label: 'Manual', type: 'leaderboard', useForcedData: false },
  { key: 'spam', label: 'Spam', type: 'spam', useForcedData: false },
  { key: 'quiz', label: 'Quiz / Poll', type: 'leaderboard', useForcedData: true },
];

function NumberInput({ x, y, width, value, min, max, onChange }) {
  return (
    <foreignObject x={x} y={y} width={width} height="28" style={{ pointerEvents: 'auto' }}>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          const v = parseInt(e.target.value, 10);
          if (!isNaN(v)) onChange?.(v);
        }}
        style={{
          width: '100%', height: '26px', borderRadius: '13px',
          border: '1px solid rgba(145,70,255,0.28)',
          background: 'rgba(145,70,255,0.12)', color: 'var(--fc-text)',
          fontSize: '11px', fontWeight: 700, padding: '0 10px',
          outline: 'none', boxSizing: 'border-box',
        }}
      />
    </foreignObject>
  );
}

const SCORE_OPTIONS = [
  { value: 'points', label: 'Points' },
  { value: 'time', label: 'Time' },
  { value: 'distance', label: 'Distance' },
];

const TIME_OPTIONS = [
  { value: 's', label: 'Seconds' },
  { value: 'ms', label: 'Min:Sec' },
  { value: 'hms', label: 'Hr:Min:Sec' },
];

function SelectControl({ x, y, width, value, options, onChange }) {
  return (
    <foreignObject x={x} y={y} width={width} height="30" style={{ pointerEvents: 'auto' }}>
      <select
        value={value}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          onChange?.(event.target.value);
        }}
        style={{
          width: '100%',
          height: '28px',
          borderRadius: '14px',
          border: '1px solid rgba(145,70,255,0.28)',
          background: 'rgba(145,70,255,0.12)',
          color: 'var(--fc-text)',
          fontSize: '10px',
          fontWeight: 700,
          padding: '0 10px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      >
        {options.map((option) => (
          <option key={String(option.value)} value={option.value} style={{ background: 'var(--fc-card-bg)', color: 'var(--fc-text)' }}>
            {option.label}
          </option>
        ))}
      </select>
    </foreignObject>
  );
}

function TextControl({ x, y, width, value, placeholder, onChange }) {
  return (
    <foreignObject x={x} y={y} width={width} height="30" style={{ pointerEvents: 'auto' }}>
      <input
        value={value}
        placeholder={placeholder}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          onChange?.(event.target.value);
        }}
        style={{
          width: '100%',
          height: '28px',
          borderRadius: '14px',
          border: '1px solid var(--fc-border)',
          background: 'var(--fc-circle-bg)',
          color: 'var(--fc-text)',
          fontSize: '10px',
          fontWeight: 600,
          padding: '0 10px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </foreignObject>
  );
}

function TogglePill({ x, y, width, active, label, activeColor, onClick }) {
  return (
    <g
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={x}
        y={y}
        width={width}
        height={TOGGLE_H}
        rx={TOGGLE_H / 2}
        fill={active ? `${activeColor}22` : C.circle}
        stroke={active ? activeColor : C.border}
        strokeWidth="1"
      />
      <circle
        cx={x + 14}
        cy={y + TOGGLE_H / 2}
        r="6"
        fill={active ? activeColor : C.border}
      />
      <text
        x={x + 28}
        y={y + TOGGLE_H / 2 + 4}
        fontSize="10"
        fontWeight="700"
        fill={active ? 'var(--fc-text)' : C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

function resolveValues(step, liveCustomisationData) {
  const cfg = step?.customisationConfig;
  const tabKey = cfg?.tabKey;
  const varKey = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const liveValues = tabKey && varKey && varName
    ? getPathValue(liveCustomisationData, [tabKey, varKey, varName])
    : null;
  const allValues = Array.isArray(liveValues)
    ? liveValues
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return allValues?.[0] || {};
}

export default function LeaderboardSettingsNode({
  step,
  selected,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onActivate,
  onFieldChange,
}) {
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const left = x - LEADERBOARD_SETTINGS_W / 2;
  const top = y - LEADERBOARD_SETTINGS_H / 2;
  const values = resolveValues(step, liveCustomisationData);

  const rawType = values?.type || 'leaderboard';
  const useForcedData = values?.useForcedData === true;
  const modeKey = rawType === 'spam' ? 'spam' : (useForcedData ? 'quiz' : 'manual');
  const scoreType = values?.scoreType || 'points';
  const ascending = values?.ascending === true;
  const timeDisplay = values?.timeDisplay || 's';
  const distanceText = values?.distanceText || 'm';
  const autoScroll = values?.autoScrollLeaderboard_CustVar !== false;
  const numberOfQuizScores = values?.numberOfQuizScores ?? 5;
  const numberOfSpamScores = values?.numberOfSpamScores ?? 5;
  const autoDeleteSpam = values?.autoDeleteSpam === true;
  const auxiliaryScoreControlX = modeKey === 'quiz' ? left + 180 : left + PAD;

  return (
    <g className="fc-node fc-node--leaderboard-settings" onClick={onActivate} style={{ cursor: 'pointer' }}>
      <rect x={left + 2} y={top + 4} width={LEADERBOARD_SETTINGS_W} height={LEADERBOARD_SETTINGS_H} rx="14" fill="#000" fillOpacity="0.28" />
      <rect
        x={left}
        y={top}
        width={LEADERBOARD_SETTINGS_W}
        height={LEADERBOARD_SETTINGS_H}
        rx="14"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.4}
      />
      <line x1={left + 1} y1={top + HEADER_H} x2={left + LEADERBOARD_SETTINGS_W - 1} y2={top + HEADER_H} stroke={C.border} strokeWidth="1" />

      <text x={left + PAD} y={top + 20} fontSize="12" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || 'Leaderboard Settings'}
      </text>
      <text x={left + PAD} y={top + 34} fontSize="9" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Manual, spam, or quiz board behaviour
      </text>

      {MODE_OPTIONS.map((option, index) => {
        const chipW = 148;
        const gap = 10;
        const chipX = left + PAD + index * (chipW + gap);
        const active = modeKey === option.key;
        return (
          <g
            key={option.key}
            onClick={(event) => {
              event.stopPropagation();
              onFieldChange?.('type', option.type);
              onFieldChange?.('useForcedData', option.useForcedData);
              if (option.key === 'spam') {
                onFieldChange?.('scoreType', 'points');
                onFieldChange?.('ascending', false);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={chipX}
              y={top + HEADER_H + 12}
              width={chipW}
              height={CHIP_H}
              rx={CHIP_H / 2}
              fill={active ? 'rgba(74,222,128,0.16)' : C.circle}
              stroke={active ? C.greenText : C.border}
              strokeWidth="1"
            />
            <text
              x={chipX + chipW / 2}
              y={top + HEADER_H + 12 + CHIP_H / 2 + 4}
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fill={active ? C.greenText : C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {option.label}
            </text>
          </g>
        );
      })}

      {(modeKey === 'manual' || modeKey === 'quiz') && (
        <>
          <text x={left + PAD} y={top + 104} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            SCORE TYPE
          </text>
          <SelectControl
            x={left + PAD}
            y={top + 112}
            width={160}
            value={scoreType}
            options={SCORE_OPTIONS}
            onChange={(value) => onFieldChange?.('scoreType', value)}
          />

          <TogglePill
            x={left + 190}
            y={top + 112}
            width={140}
            active={!ascending}
            label="Descending"
            activeColor={C.orange}
            onClick={() => onFieldChange?.('ascending', false)}
          />
          <TogglePill
            x={left + 340}
            y={top + 112}
            width={140}
            active={ascending}
            label="Ascending"
            activeColor={C.greenText}
            onClick={() => onFieldChange?.('ascending', true)}
          />

          {scoreType === 'distance' && (
            <>
              <text x={auxiliaryScoreControlX} y={top + 158} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                DISTANCE TEXT
              </text>
              <TextControl
                x={auxiliaryScoreControlX}
                y={top + 166}
                width={140}
                value={distanceText}
                placeholder="m"
                onChange={(value) => onFieldChange?.('distanceText', value)}
              />
            </>
          )}

          {scoreType === 'time' && (
            <>
              <text x={auxiliaryScoreControlX} y={top + 158} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                TIME DISPLAY
              </text>
              <SelectControl
                x={auxiliaryScoreControlX}
                y={top + 166}
                width={190}
                value={timeDisplay}
                options={TIME_OPTIONS}
                onChange={(value) => onFieldChange?.('timeDisplay', value)}
              />
            </>
          )}
        </>
      )}

      {/* Auto-scroll follows the shared score controls for manual and quiz/poll. */}
      <TogglePill
        x={left + PAD}
        y={modeKey === 'manual' || modeKey === 'quiz' ? top + 200 : top + 96}
        width={180}
        active={autoScroll}
        label="Auto-scroll Board"
        activeColor={C.purple}
        onClick={() => onFieldChange?.('autoScrollLeaderboard_CustVar', !autoScroll)}
      />

      {modeKey === 'quiz' && (
        <>
          <text x={left + PAD} y={top + 158} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            QUIZ / POLL SCORES SHOWN
          </text>
          <NumberInput
            x={left + PAD}
            y={top + 166}
            width={100}
            value={numberOfQuizScores}
            min={1}
            max={20}
            onChange={(value) => onFieldChange?.('numberOfQuizScores', value)}
          />
        </>
      )}
      {modeKey === 'spam' && (
        <>
          <text x={left + PAD} y={top + 138} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            SPAM SCORES SHOWN
          </text>
          <NumberInput
            x={left + PAD}
            y={top + 146}
            width={100}
            value={numberOfSpamScores}
            min={1}
            max={20}
            onChange={(value) => onFieldChange?.('numberOfSpamScores', value)}
          />
          <TogglePill
            x={left + PAD + 110}
            y={top + 146}
            width={160}
            active={autoDeleteSpam}
            label="Clear on Hide"
            activeColor={C.orange}
            onClick={() => onFieldChange?.('autoDeleteSpam', !autoDeleteSpam)}
          />
        </>
      )}

      {modeKey === 'manual' && (
        <text x={left + PAD} y={top + 246} fontSize="9" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          Manual mode — edit entries on the board below.
        </text>
      )}
    </g>
  );
}
